import React, {FC, useEffect, useState} from "react";
import {useAxios} from "../../utils/hooks";
import useSafeState from "../../utils/safe-state";
import '../../styles/Onboarding.css'
import {Trans} from "@lingui/macro";
import {Breadcrumb, Button, Divider, Input, message, Popconfirm, Tooltip} from "antd";
import {EyeInvisibleOutlined, EyeOutlined} from "@ant-design/icons";
import {openNotification} from "../../utils/openNotification";
import {stringify} from "qs";
import {useKeycloak} from "@react-keycloak/web";
import { useHistory } from "react-router-dom";

const OnboardingScreen: FC = () => {

    const {keycloak} = useKeycloak();
    const history = useHistory();
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const searchParams = new URLSearchParams(window.location.search);
    const email = searchParams.get('email');
    const id = searchParams.get('id');
    const clientSecret = searchParams.get('scrt');
    // const { showModal, hideModal, modalState } = useMessageModal();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isValidating, setIsValidating] = useState(true);
    const [isUrlValid, setIsUrlValid] = useState(false);

    const [trigger, setTrigger] = useSafeState(false);


    useEffect(() => {
        validateUrl();
    }, [email, id, clientSecret]);


    const validateUrl = async () => {
        if (!email || !id || !clientSecret) {
            setIsValidating(false);
            setIsUrlValid(false);
            return;
        }

        try {

            if (axiosInstance.current != null) {
                axiosInstance
                    .current
                    .get(
                        '/public/onboarding/is-valid?email=' + encodeURIComponent(email) +
                        '&supplierCode=' + encodeURIComponent(id) +
                        '&token=' + encodeURIComponent(clientSecret)
                    ).then((response) => {
                    setIsUrlValid(response.data);
                }).catch((error) => {
                    openNotification('error', error);
                    setIsUrlValid(false);
                });
            }
        } catch (error) {
            console.error('Network error during validation:', error);
            setIsUrlValid(false);
        } finally {
            setIsValidating(false);
        }
    };


    const handlePasswordAction = (action: any) => {
        openNotification('error', 'For security reasons, you cannot {action} text in password fields. Please type your password manually.');
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();

        if (!isUrlValid) {
            openNotification('error', 'This registration link is invalid. Please refresh the page or contact your administrator.');
            return;
        }

        if (password !== confirmPassword) {
            openNotification('error', 'The passwords you entered do not match. Please check and try again.');
            return;
        }

        const formData = new FormData(e.target);
        const userData = {
            email: email,
            supplierCode: id,
            username: formData.get('username'),
            password: password,
            token: clientSecret
        };

        try {
            if (axiosInstance.current != null) {

                axiosInstance.current.post(
                    `/public/onboarding/create-supplier-admin`,
                    userData, config
                )
                    .then((response) => {

                        openNotification('success', 'Your account has been successfully created! Redirecting to login...');

                        history.push("/dashboard");

                    }).catch((error) => {
                    console.log(error);
                    openNotification('error', error)
                });
            }

        } catch (error) {
            console.error('Network error:', error);
            openNotification('error', error);
        }
    };

    if (!email || !id || !clientSecret) {
        return (
            <div className="onboarding-error">
                <h1><Trans>Error: Missing Parameters</Trans></h1>
                <p><Trans>Please ensure that the URL contains the required
                    parameters: <code>email</code>, <code>id</code>, and <code>scrt</code>.</Trans></p>
            </div>
        );
    }

    if (isValidating) {
        return (
            <div className="onboarding-container">
                <div className="onboarding-header">
                    <h1>EUDR</h1>
                    <h2><Trans>Validating Registration Link...</Trans></h2>
                    <p><Trans>Please wait while we verify your registration link</Trans></p>
                </div>
                <div className="onboarding-form">
                    <div style={{textAlign: 'center', padding: '2rem'}}>
                        <div style={{
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #3498db',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto'
                        }}></div>
                        <p style={{marginTop: '1rem', color: '#666'}}><Trans>Validating...</Trans></p>
                    </div>
                </div>
            </div>
        );
    }

    if (!isUrlValid) {
        return (
            <div className="onboarding-error">
                <h1><Trans>Invalid Registration Link</Trans></h1>
                <p><Trans>This registration link is invalid or has expired. Please contact your administrator for a new
                    registration link.</Trans></p>
            </div>
        );
    }

    return (

        (keycloak != null && keycloak.authenticated)
            ? <>You are already registered on the portal.</>
            :
            <div className="onboarding-container">

                <div className="onboarding-header">
                    <h2><Trans>Account Setup</Trans></h2>
                    <p><Trans>Complete your registration to get started</Trans></p>
                </div>

                <form className="onboarding-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="email"><Trans>Email Address</Trans></label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            disabled={true}
                            value={email}
                            readOnly={!!email}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="supplier-code"><Trans>Supplier Code</Trans></label>
                        <input
                            type="text"
                            id="supplier-code"
                            name="supplier-code"
                            disabled={true}
                            value={id}
                            readOnly={!!id}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="username"><Trans>Username</Trans></label>
                        <input
                            type="text"
                            id="username"
                            name="username"
                            autoFocus={true}
                            placeholder="Choose a username"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password"><Trans>Password</Trans></label>
                        <div className="password-input-container">
                            <Input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                placeholder="Create a secure password"
                                value={password}
                                onChange={(e: any) => setPassword(e.target.value)}
                                onCopy={(e: any) => {
                                    e.preventDefault();
                                    handlePasswordAction('copy');
                                }}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    handlePasswordAction('paste');
                                }}
                                onDrag={(e) => {
                                    e.preventDefault();
                                    handlePasswordAction('drag');
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    handlePasswordAction('drop');
                                }}
                                autoComplete="new-password"
                                required
                            />
                            <button
                                type="button"
                                tabIndex={-1}
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOutlined/> : <EyeInvisibleOutlined/>}
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword"><Trans>Confirm Password</Trans></label>
                        <div className="password-input-container">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder="Re-enter your password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                onCopy={(e) => {
                                    e.preventDefault();
                                    handlePasswordAction('copy');
                                }}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    handlePasswordAction('paste');
                                }}
                                onDrag={(e) => {
                                    e.preventDefault();
                                    handlePasswordAction('drag');
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    handlePasswordAction('drop');
                                }}
                                autoComplete="new-password"
                                required
                            />
                            <button
                                type="button"
                                tabIndex={-1}
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? <EyeOutlined/> : <EyeInvisibleOutlined/>}
                            </button>
                        </div>
                    </div>

                    <button type="submit" className="submit-button">
                        <Trans>Complete Registration</Trans>
                    </button>
                </form>
            </div>
)
}
export default OnboardingScreen;
